import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import './style.scss';
import Layout from 'components/Layout/Layout';
import Table from 'components/common/Table';
import SearchBar from 'components/common/SearchBar';
import FlagFilter from 'components/common/FlagFilter';
import api from 'services/api';

const Requesters = () => {
  const currentLogger = useSelector(state => state.global.currentLogger);

  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [curPage, setCurPage] = useState(1);
  const [nRequests] = useState(50);
  const [hasMore, setHasMore] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('totalRequests');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedFlags, setSelectedFlags] = useState(0);
  const [actionFilter, setActionFilter] = useState('');

  const formatTimestamp = (timestamp) => {
    if (!timestamp) {
      return;
    }

    return new Date(timestamp).toLocaleString();
  };

  const parseData = (data) => {
    if (!data.requesters) {
      return;
    }
    const { requesters } = data;

    const dataParsed = [];

    for (let i = 0; i < requesters.length; i++) {
      dataParsed.push([
        i,
        { ipAddress: requesters[i].ipAddress, loggerId: currentLogger, requesterId: requesters[i].id },
        requesters[i].avgTimeTaken,
        requesters[i].totalRequests,
        formatTimestamp(requesters[i].firstAccess),
        formatTimestamp(requesters[i].lastAccess),
        requesters[i].groupName,
        requesters[i].action,
      ]);
    }
    return dataParsed;
  };

  /**
   * Fetches for log data.
   */
  const fetchRequesters = useCallback((page = 1) => {
    console.log('fetching data...');
    setIsError(false);
    api.getRequesters(
      currentLogger,
      nRequests,
      page,
      searchQuery,
      sortColumn,
      sortOrder,
      selectedFlags,
      actionFilter,
    res => {
      setIsLoading(false);
      if (res.data.status === 200) {
        if (res.data.requesters.length < nRequests) {
          setHasMore(false);
        }
        if (page !== 1) {
          setData(previousData => ({
            status: res.data.status,
            groups: previousData.groups,
            requesters: [...previousData.requesters, ...res.data.requesters],
          }));
        } else {
          setData(res.data);
        }
      } else {
        setIsError(true);
      }
    });
  }, [currentLogger, nRequests, searchQuery, sortColumn, sortOrder, selectedFlags, actionFilter]);

  const handleSearch = (searchItem) => {

    setCurPage(1);
    setHasMore(true);
    setSearchQuery(searchItem);
  };

  const handleActionFilter = (action) => {
    setCurPage(1);
    setHasMore(true);
    setActionFilter(action);
  };

  const incrementPage = () => {
    const nextPage = curPage + 1;
    fetchRequesters(nextPage);
    setCurPage(nextPage);
  };

  const handleSort = (column) => {
    setSortOrder(previousOrder =>
      column === sortColumn && previousOrder === 'desc' ? 'asc' : 'desc'
    );
    setSortColumn(column);
    setCurPage(1);
    setHasMore(true);
  };

  /**
   * Calls fetchLogs onload.
   */
  useEffect(() => {
    setIsLoading(true);
    fetchRequesters();
  }, [fetchRequesters]);

  return (
    <Layout title={'Requesters'} loading={isLoading} error={isError}>
      {isError}
      <div
        className="activity-filter-row requesters-filter-row"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <SearchBar onSearch={handleSearch} initialValue={searchQuery} />
        <div className="field">
          <label className="label" htmlFor="action-filter">Action</label>
          <div className="select is-small">
            <select id="action-filter" onChange={(e) => handleActionFilter(e.target.value)} value={actionFilter}>
              <option value="">All</option>
              <option value="allow">allow</option>
              <option value="challenge">challenge</option>
              <option value="block">block</option>
            </select>
          </div>
        </div>
      </div>
      <FlagFilter value={selectedFlags} onChange={setSelectedFlags} style={{ marginBottom: '1em' }} />
      <Table
        headerAttributes={[
          'IP Address', 'Avg Time per Request (ms)', 'Total Requests',
          'First Access', 'Last Access', 'Group', 'Action'
        ]}
        data={parseData(data)}
        drawIpAddressPosition={1}
        callBackUrl={incrementPage}
        hasMore={hasMore}
        sortableColumns={[
          'ipAddress',
          null,
          'totalRequests',
          'firstAccess',
          'lastAccess',
          'groupName',
          'action',
        ]}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={handleSort}
      />
    </Layout>
  );
};

export default Requesters;
